import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ConfirmModal, IReferralOrganisation, Page, SortOrder, Table, useQueryParamsContext } from '../../../shared';
import { SearchInput } from '../../../shared/components/search/search.component';
import { useDeleteReferralOrganisation, useReferralOrganisationList } from '../../hooks/referral-organisation.hook';
import { Edit } from '@mui/icons-material';

export const ReferralOrganisationsPage: FC = () => {
    const { t } = useTranslation();
    const {
        queryParams: { page, pageSize, sort, search },
        debouncedQueryParams,
        setPage,
        setPageSize,
        setSort,
        setSearch,
    } = useQueryParamsContext('referral-organisations', {
        sort: [{ field: 'name', sort: 'asc' }],
    });

    const { isPending, data: referralOrganisations } = useReferralOrganisationList({
        page,
        pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
        search: debouncedQueryParams.search,
    });

    const { mutate: deleteOrganisation } = useDeleteReferralOrganisation();

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('nameLabel'), minWidth: 150, flex: 1 },
        { field: 'code', headerName: t('codeLabel'), minWidth: 150, flex: 1 },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id } }: GridCellParams<IReferralOrganisation>) => (
                <>
                    <IconButton component={Link} to={`/admin/referral-organisations/edit/${id}`}>
                        <Edit />
                    </IconButton>
                    <ConfirmModal
                        onConfirm={() =>
                            deleteOrganisation({
                                id,
                                messages: {
                                    onDeleteSuccessMessage: t('deleteReferralOrganisationSuccess'),
                                    onDeleteErrorMessage: t('deleteReferralOrganisationError'),
                                },
                            })
                        }
                        button={
                            <IconButton aria-label="delete">
                                <DeleteOutlineIcon />
                            </IconButton>
                        }
                        title={t('referralOrganisationDeleteWarningTitle')}
                        text={t('referralOrganisationDeleteWarningText')}
                    />
                </>
            ),
        },
    ];
    return (
        <Page
            title={t('referralOrganisations')}
            actions={[
                <SearchInput value={search} setValue={setSearch} />,
                <Button
                    component={Link}
                    to="/admin/referral-organisations/edit"
                    color="primary"
                    variant="contained"
                    data-testid="newReferralOrganisation"
                >
                    {t('newReferralOrganisation')}
                </Button>,
            ]}
        >
            <Table
                rows={referralOrganisations?.data || []}
                columns={columns}
                rowCount={referralOrganisations?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
            />
        </Page>
    );
};
